import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import * as FiIcons from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Query } from '../../types/Query';
import { fetchVendorQuoteThread, sendVendorQuote } from '../../api/VendorQuote';
import { VendorQuoteMessage } from '../../types/VendorQuote';
import { formatLocalDate, formatLocalDateTime } from '../../utils/dateFormat';
import { getQueryStatusClassName, getQueryStatusLabel } from '../../utils/queryStatus';
import { groupMessagesIntoThreads, VendorQuoteThread } from '../../utils/vendorQuoteThreads';
import '../../css/modal.css';

type VendorQuotationModalProps = {
  isOpen: boolean;
  query: Query | null;
  onClose: () => void;
  backdropClassName?: string;
};

const FiCopyIcon = FiIcons.FiCopy as unknown as React.FC;

const sanitizeEmailHtml = (html: string) => {
  if (!html.trim()) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'hr', 'i', 'li', 'ol', 'p', 'pre', 'span', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'ul',
    ],
    ALLOWED_ATTR: ['colspan', 'href', 'rel', 'rowspan', 'target', 'title'],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
};

/**
 * Best-effort extraction of the visible reply content from an email.
 * We try to strip off quoted history like "On Thu, ... wrote:" or
 * Gmail-style quoted blocks so that the conversation view focuses on
 * the latest reply text.
 */
const extractReplyPortion = (html?: string, fallbackText?: string) => {
  const rawHtml = (html || '').trim();
  if (rawHtml && typeof DOMParser !== 'undefined') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');

    // Remove common quoted sections
    doc.querySelectorAll('.gmail_quote, blockquote').forEach((el) => el.remove());

    // Also remove paragraphs/divs that look like "On ... wrote:"
    const nodes = Array.from(doc.querySelectorAll('p, div, span'));
    let cutIndex = -1;
    nodes.forEach((node, index) => {
      const text = node.textContent || '';
      if (/^on .+wrote:$/i.test(text.trim()) || /^-----original message-----$/i.test(text.trim())) {
        cutIndex = cutIndex === -1 ? index : cutIndex;
      }
    });
    if (cutIndex >= 0) {
      for (let i = cutIndex; i < nodes.length; i += 1) {
        const node = nodes[i];
        if (node.parentElement) {
          node.parentElement.removeChild(node);
        }
      }
    }

    const cleaned = doc.body.innerHTML.trim();
    if (cleaned) return sanitizeEmailHtml(cleaned);
  }

  // Fallback for plain text bodies – trim everything after "On ... wrote:"
  const text = (fallbackText || '').trim();
  if (!text) return '';
  const lines = text.split('\n');
  const visible: string[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (/^on .+wrote:$/i.test(line.trim()) || /^-----original message-----$/i.test(line.trim())) {
      break;
    }
    visible.push(line);
  }
  const trimmed = visible.join('\n').trim();
  return trimmed.replace(/\n/g, '<br />');
};

const copyToClipboard = async (value: string) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
    const el = document.createElement('textarea');
    el.value = value;
    el.setAttribute('readonly', 'true');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return true;
  } catch {
    return false;
  }
};

const VendorQuotationModal: React.FC<VendorQuotationModalProps> = ({
  isOpen,
  query,
  onClose,
  backdropClassName,
}) => {
  const [messages, setMessages] = useState<VendorQuoteMessage[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const toEmailInputRef = useRef<HTMLInputElement>(null);
  const hasUserEditedTo = useRef(false);

  const queryDisplayId = query?.referenceId || (query ? `Q${query.id}` : '—');

  useEffect(() => {
    if (!isOpen || !query) return;
    hasUserEditedTo.current = false;
    setToEmail('');
    setSubject(`Vendor Quotation for query ${queryDisplayId} - ${query.location}`);
    setBody(`Hello,\n\nPlease share quotation for ${query.location}.\nTravel Date: ${formatLocalDate(query.queryDate)}\nPAX: ${query.pax}\n\nThanks.`);
    setIsComposeOpen(false);
    setSelectedThreadId(null);
  }, [isOpen, query, queryDisplayId]);

  useEffect(() => {
    if (!isOpen || !query) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchVendorQuoteThread(query.id);
        if (!mounted) return;
        setMessages(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (mounted) {
          setMessages([]);
          toast.error(e?.message || 'Failed to load vendor messages');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isOpen, query]);

  const threads = useMemo(() => {
    if (!query) return [];
    return groupMessagesIntoThreads(messages, query.id);
  }, [messages, query]);

  useEffect(() => {
    if (!threads.length) {
      setSelectedThreadId(null);
      return;
    }
    if (selectedThreadId && threads.some((thread) => thread.id === selectedThreadId)) return;
    setSelectedThreadId(threads[0].id);
  }, [threads, selectedThreadId]);

  const activeThread = useMemo<VendorQuoteThread | null>(() => {
    if (!threads.length) return null;
    if (selectedThreadId) {
      return threads.find((thread) => thread.id === selectedThreadId) || threads[0];
    }
    return threads[0];
  }, [threads, selectedThreadId]);

  const suggestedVendorEmail = activeThread?.vendor.email || '';

  useEffect(() => {
    if (!isOpen) return;
    if (!hasUserEditedTo.current && !toEmail.trim() && suggestedVendorEmail) {
      setToEmail(suggestedVendorEmail);
    }
  }, [isOpen, suggestedVendorEmail, toEmail]);

  useEffect(() => {
    if (!isComposeOpen) return;
    const timer = setTimeout(() => toEmailInputRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [isComposeOpen]);

  const safeHtmlById = useMemo(() => {
    const map = new Map<number, string>();
    activeThread?.messages.forEach((message) => {
      const replyHtml = extractReplyPortion(message.bodyHtml, message.bodyText);
      const html = replyHtml?.trim();
      if (!html) return;
      map.set(message.id, sanitizeEmailHtml(html));
    });
    return map;
  }, [activeThread]);

  const handleSend = useCallback(async () => {
    if (!query) return;
    if (!toEmail.trim() || !subject.trim() || !body.trim()) {
      toast.error('Please fill To, Subject, and Body');
      return;
    }

    setSending(true);
    try {
      await sendVendorQuote(query.id, {
        toEmail: toEmail.trim(),
        subject: subject.trim(),
        bodyText: body.trim(),
        bodyHtml: body.trim().replace(/\n/g, '<br />'),
      });

      const data = await fetchVendorQuoteThread(query.id);
      const safeData = Array.isArray(data) ? data : [];
      setMessages(safeData);
      setBody('');
      setIsComposeOpen(false);
      hasUserEditedTo.current = false;
      toast.success('Vendor RFQ sent');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send vendor quotation');
    } finally {
      setSending(false);
    }
  }, [query, toEmail, subject, body]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      const isCmdOrCtrlEnter = (e.metaKey || e.ctrlKey) && e.key === 'Enter';
      if (e.key === 'Escape') {
        e.preventDefault();
        if (isComposeOpen) {
          setIsComposeOpen(false);
          return;
        }
        onClose();
      }

      if (isComposeOpen && isCmdOrCtrlEnter) {
        const target = e.target as Node;
        if (!modalRef.current?.contains(target)) return;
        e.preventDefault();
        handleSend();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, isComposeOpen, onClose, handleSend]);

  if (!isOpen || !query) return null;

  return (
    <div className={`modal-backdrop ${backdropClassName || ''}`.trim()} onClick={onClose}>
      <div
        ref={modalRef}
        className="modal modal-lg vendor-quote-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendor-rfq-title"
      >
        <button className="close-btn" onClick={onClose} aria-label="Close">&times;</button>

        <div className="vendor-quote-header">
          <div className="vendor-quote-title-row">
            <h2 id="vendor-rfq-title">Vendor RFQ</h2>
            <span className={`vendor-quote-query-status ${getQueryStatusClassName(query.status)}`}>
              {getQueryStatusLabel(query.status)}
            </span>
          </div>
          <div className="vendor-quote-context-grid">
            <div className="vendor-quote-context-item">
              <span className="vendor-quote-context-label">Query ID</span>
              <div className="vendor-quote-context-value">
                <span>{queryDisplayId}</span>
                <button
                  type="button"
                  className="vendor-quote-copy-btn"
                  onClick={async () => {
                    const didCopy = await copyToClipboard(queryDisplayId);
                    if (didCopy) toast.success('Query ID copied');
                    else toast.error('Could not copy Query ID');
                  }}
                  aria-label="Copy Query ID"
                  title="Copy Query ID"
                >
                  <FiCopyIcon />
                </button>
              </div>
            </div>
            <div className="vendor-quote-context-item">
              <span className="vendor-quote-context-label">Destination</span>
              <span className="vendor-quote-context-value">{query.location || '—'}</span>
            </div>
            <div className="vendor-quote-context-item">
              <span className="vendor-quote-context-label">Travel Date</span>
              <span className="vendor-quote-context-value">{formatLocalDate(query.queryDate)}</span>
            </div>
            <div className="vendor-quote-context-item">
              <span className="vendor-quote-context-label">PAX</span>
              <span className="vendor-quote-context-value">{query.pax ?? '—'}</span>
            </div>
          </div>
        </div>

        <div className="vendor-quote-layout">
          <aside className="vendor-quote-thread" aria-label="Thread list">
            <div className="vendor-quote-section-title">Threads</div>

            {loading && (
              <div className="vendor-quote-skeleton-list" aria-busy="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="vendor-quote-skeleton-item" />
                ))}
              </div>
            )}

            {!loading && threads.length === 0 && (
              <div className="vendor-quote-empty-state">
                <p>No vendor threads yet.</p>
                <p className="vendor-quote-empty-hint">Compose an RFQ to start the first thread.</p>
              </div>
            )}

            {!loading && threads.map((thread) => {
              const isActive = activeThread?.id === thread.id;
              return (
                <button
                  key={thread.id}
                  type="button"
                  className={`vendor-quote-thread-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSelectedThreadId(thread.id)}
                >
                  <div className="vendor-quote-thread-top">
                    <div className="vendor-quote-thread-vendor-wrap">
                      <div className="vendor-quote-thread-vendor">{thread.vendor.name}</div>
                      <div className="vendor-quote-thread-email" title={thread.vendor.email || ''}>
                        {thread.vendor.email || '—'}
                      </div>
                    </div>
                    <span className={`vendor-quote-thread-status ${thread.status.toLowerCase()}`}>{thread.status}</span>
                  </div>

                  <div className="vendor-quote-thread-title">{thread.subject}</div>

                  <div className="vendor-quote-thread-preview">{thread.preview || 'No preview available.'}</div>

                  <div className="vendor-quote-thread-footer">
                    <span>{formatLocalDateTime(thread.lastActivity)}</span>
                    <span className="vendor-quote-thread-count">{thread.messageCount}</span>
                  </div>
                </button>
              );
            })}
          </aside>

          <section className="vendor-quote-detail" aria-label="Thread view">
            <div className="vendor-quote-detail-top">
              <div className="vendor-quote-section-title">Conversation</div>
              <button
                type="button"
                className="vendor-quote-compose-toggle"
                onClick={() => setIsComposeOpen((prev) => !prev)}
              >
                {isComposeOpen ? 'Hide Compose' : 'Compose RFQ'}
              </button>
            </div>

            {activeThread ? (
              <div className="vendor-quote-conversation">
                <div className="vendor-quote-conversation-head">
                  <div className="vendor-quote-message-subject">{activeThread.subject}</div>
                  <div className="vendor-quote-message-meta">
                    {activeThread.vendor.name} {activeThread.vendor.email ? `(${activeThread.vendor.email})` : ''} · {activeThread.messageCount} {activeThread.messageCount === 1 ? 'message' : 'messages'}
                  </div>
                </div>

                <div className="vendor-quote-mail-list">
                  {activeThread.messages.map((message) => {
                    const safeHtml = safeHtmlById.get(message.id);
                    const directionLabel = message.direction === 'outbound'
                      ? 'Sent'
                      : message.direction === 'inbound'
                        ? 'Received'
                        : 'Message';

                    return (
                      <article
                        key={message.id}
                        className={`vendor-quote-mail-card ${message.direction === 'outbound' ? 'outbound' : 'inbound'}`}
                      >
                        <div className="vendor-quote-mail-meta-row">
                          <span className="vendor-quote-mail-direction">{directionLabel}</span>
                          <span title={message.fromEmail || ''}><strong>From:</strong> {message.fromEmail || '—'}</span>
                          <span title={message.toEmail || ''}><strong>To:</strong> {message.toEmail || '—'}</span>
                          <span>{formatLocalDateTime(message.createdAt)}</span>
                        </div>

                        <div className="vendor-quote-message-body">
                          {safeHtml ? (
                            <div
                              className="vendor-quote-message-html"
                              dangerouslySetInnerHTML={{ __html: safeHtml }}
                            />
                          ) : message.bodyText ? (
                            <pre className="vendor-quote-message-text">{message.bodyText}</pre>
                          ) : (
                            '—'
                          )}
                        </div>

                        {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                          <div className="vendor-quote-mail-attachments">
                            {message.attachments.map((attachment, attachmentIndex) => {
                              const label = attachment.name || `Attachment ${attachmentIndex + 1}`;
                              if (attachment.url) {
                                return (
                                  <a
                                    key={`${message.id}-${label}-${attachmentIndex}`}
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="vendor-quote-mail-attachment"
                                  >
                                    {label}
                                  </a>
                                );
                              }
                              return (
                                <span
                                  key={`${message.id}-${label}-${attachmentIndex}`}
                                  className="vendor-quote-mail-attachment disabled"
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="vendor-quote-empty vendor-quote-empty-neutral">Select a thread to view messages.</div>
            )}

            {isComposeOpen && (
              <div className="vendor-quote-compose">
                <div className="vendor-quote-section-title">Compose Email</div>
                <p className="vendor-quote-compose-hint">Press Cmd+Enter (Mac) or Ctrl+Enter (Windows) to send</p>

                <label htmlFor="vendor-quote-to">To</label>
                <input
                  ref={toEmailInputRef}
                  id="vendor-quote-to"
                  type="email"
                  value={toEmail}
                  onChange={(e) => {
                    hasUserEditedTo.current = true;
                    setToEmail(e.target.value);
                  }}
                  placeholder="vendor@email.com"
                  aria-required="true"
                />

                <label htmlFor="vendor-quote-subject">Subject</label>
                <input
                  id="vendor-quote-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  aria-required="true"
                />

                <label htmlFor="vendor-quote-body">Body</label>
                <textarea
                  id="vendor-quote-body"
                  className="vendor-quote-compose-body"
                  rows={6}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  aria-required="true"
                />

                <div className="modal-actions">
                  <button type="button" className="secondary" onClick={() => setIsComposeOpen(false)}>Cancel</button>
                  <button type="button" className="primary" onClick={handleSend} disabled={sending}>
                    {sending ? 'Sending…' : 'Send RFQ'}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default VendorQuotationModal;
